import { useState, useEffect } from 'react';
import PanelLayout from '@/components/PanelLayout';
import axios from 'axios';
import { API } from '@/utils/api';
import { Loader2, MessageCircle, CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp, User, ArrowRight, Sparkles, Mail, Inbox } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const STEP_LABELS = { 0: 'Auto-reply', 1: 'Zainteresowanie', 2: 'Oferta', 3: 'Ostatnia szansa' };
const STEP_COLORS = {
  0: { bg: '#F47A3B12', border: '#F47A3B30', text: '#F47A3B' },
  1: { bg: '#3B82F612', border: '#3B82F630', text: '#3B82F6' },
  2: { bg: '#EAB30812', border: '#EAB30830', text: '#EAB308' },
  3: { bg: '#EF444412', border: '#EF444430', text: '#EF4444' },
};

export default function LeadNurturing() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/autonomous/nurturing-pipeline`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  return (
    <PanelLayout>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="lead-nurturing-page">
        <div className="text-center mb-2">
          <span className="inline-block px-3 py-1 rounded-full bg-[#F47A3B]/10 text-[#F47A3B] text-xs font-semibold mb-3">Zarabiaj więcej</span>
          <h1 className="text-3xl font-bold text-zinc-900 mb-2">Zamień zapytania w klientów</h1>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">Automatyczne follow-upy w 3 krokach — system sam kontaktuje się z leadami, aż odpowiedzą.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" /></div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {[
                { label: 'Łącznie', value: data?.total || 0, color: '#F47A3B' },
                { label: 'Aktywne', value: data?.active || 0, color: '#3B82F6' },
                { label: 'Zakończone', value: data?.completed || 0, color: '#10B981' },
                { label: 'Zatrzymane', value: data?.stopped || 0, color: '#94A3B8' },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-zinc-100 rounded-2xl p-4 text-center" data-testid={`nurture-stat-${i}`}>
                  <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Pipeline steps visualization */}
            <div className="bg-white border border-zinc-100 rounded-2xl p-6 mb-6">
              <h3 className="text-sm font-bold text-zinc-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#F47A3B]" /> Jak system kontaktuje klienta
              </h3>
              <div className="flex items-center gap-2">
                {(data?.steps || []).map((step, i) => {
                  const count = (data?.leads || []).filter(l => (l.nurture_step || 0) === step.step && !l.nurture_completed && !l.nurture_stopped).length;
                  const sc = STEP_COLORS[step.step] || STEP_COLORS[0];
                  return (
                    <div key={i} className="flex-1 text-center">
                      <div className="rounded-xl py-3 px-2" style={{ background: sc.bg, border: `1px solid ${sc.border}` }}>
                        <p className="text-lg font-bold" style={{ color: sc.text }}>{count}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{step.label}</p>
                        <p className="text-[10px] text-zinc-400">po {step.delay}</p>
                      </div>
                      {i < (data?.steps?.length || 0) - 1 && (
                        <div className="text-zinc-300 text-lg mt-1">→</div>
                      )}
                    </div>
                  );
                })}
                <div className="flex-1 text-center">
                  <div className="rounded-xl py-3 px-2 bg-emerald-50 border border-emerald-100">
                    <p className="text-lg font-bold text-emerald-600">{data?.completed || 0}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">Zakończone</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Leads list */}
            <div className="bg-white border border-zinc-100 rounded-2xl p-6">
              <h3 className="text-sm font-bold text-zinc-900 mb-4">Zapytania w obsłudze</h3>
              {(!data?.leads || data.leads.length === 0) ? (
                <div className="py-4" data-testid="nurturing-empty">
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-7 h-7 text-[#F47A3B]" />
                    </div>
                    <p className="text-[15px] text-zinc-500 max-w-sm mx-auto">
                      System automatycznie kontaktuje się z osobami, które napisały — aż odpowiedzą lub kupią.
                    </p>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="bg-zinc-50 rounded-2xl p-5 mb-5 text-left">
                    <h3 className="text-sm font-bold text-zinc-900 mb-3">Jak to działa?</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Inbox className="w-4 h-4 text-[#F47A3B]" />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-zinc-800">Klient pisze do Ciebie</p>
                          <p className="text-[12px] text-zinc-500">Każde zapytanie trafia do systemu jako lead</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Mail className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-zinc-800">System odpowiada automatycznie</p>
                          <p className="text-[12px] text-zinc-500">Spersonalizowana odpowiedź w ciągu minut</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Sparkles className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-zinc-800">3 follow-upy aż do odpowiedzi</p>
                          <p className="text-[12px] text-zinc-500">Zainteresowanie → oferta → ostatnia szansa</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-center">
                    <p className="text-[12px] text-zinc-400 mb-3">Leady pojawią się tutaj automatycznie po pierwszych zapytaniach</p>
                    <button onClick={() => window.location.href = '/panel/publikacja'}
                      className="inline-flex items-center gap-2 bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-8 py-3 text-[14px] font-bold shadow-lg shadow-[#F47A3B]/20 transition-all"
                      data-testid="nurturing-empty-cta">
                      Opublikuj post <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.leads.map(lead => {
                    const step = lead.nurture_step || 0;
                    const sc = STEP_COLORS[step] || STEP_COLORS[0];
                    const isOpen = expanded[lead.id];
                    return (
                      <div key={lead.id} className="border border-zinc-100 rounded-xl overflow-hidden" data-testid={`nurture-lead-${lead.id}`}>
                        <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-50 transition-colors" onClick={() => toggle(lead.id)}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: sc.bg, color: sc.text }}>
                              {step}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-zinc-900">{lead.name || lead.first_name || lead.email || 'Anonimowy'}</p>
                              <p className="text-xs text-zinc-400">{lead.email || lead.phone || ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {lead.nurture_completed && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                            {lead.nurture_stopped && <XCircle className="w-4 h-4 text-zinc-400" />}
                            {!lead.nurture_completed && !lead.nurture_stopped && <Clock className="w-4 h-4 text-blue-500" />}
                            {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                          </div>
                        </div>
                        {isOpen && lead.nurture_history?.length > 0 && (
                          <div className="px-4 pb-4 space-y-2 border-t border-zinc-50">
                            {lead.nurture_history.map((h, i) => (
                              <div key={i} className="bg-zinc-50 rounded-lg p-3 mt-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: (STEP_COLORS[h.step] || STEP_COLORS[0]).text }}>
                                    Krok {h.step} — {h.intent}
                                  </span>
                                  <span className="text-[10px] text-zinc-400">{h.generated_at ? new Date(h.generated_at).toLocaleDateString('pl-PL') : ''}</span>
                                </div>
                                <p className="text-xs text-zinc-600 whitespace-pre-wrap">{h.text}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PanelLayout>
  );
}
